/**
 * ĐÒN VÕ CẬN CHIẾN — mỗi class (và mỗi loại quái) ra một BÀI VÕ riêng,
 * để cận chiến nhìn ra "đánh võ" chứ không phải một kiểu vung tay.
 *
 * Vì sao tách bảng này ra khỏi `art/pose.ts`:
 *   - Ở đây chỉ khai BÀI VÕ và DANH TÍNH từng thế (tên, chi nào ra đòn, loại đòn).
 *   - Hình dáng cụ thể (góc xương, nhịp lấy đà / ra đòn / thu về) nằm ở `pose.ts`.
 * Nhờ vậy thêm một thế võ = thêm 1 dòng ở đây + 1 case ở `pose.ts`, KHÔNG tốn ảnh.
 *
 * ⚠️ BÀI HỌC: `meleeMoveFor(u.kind, u.attackMove)` chạy cho cả TƯỚNG lẫn QUÁI, nên
 * bảng `MELEE_COMBOS` phải phủ hết mọi kind trong `data/enemies.ts`
 * (thug · runner · brawler · gunner · spawner · brat · eliteBrat · stageBoss).
 * Thiếu một kind ⇒ rơi vào `['punch']` và con quái đó đánh mãi một thế.
 */
export const MELEE_COMBOS: Record<string, string[]> = {
  // ─────────────────────────────────────────────────── 5 CLASS NGƯỜI CHƠI
  // Dân tập gym: nặng, chậm, đánh đủ tầng (tay — gối — đầu — chưởng)
  strength: ['punch', 'uppercut', 'hook', 'knee', 'elbow', 'headbutt', 'palm'],
  // Gái ngành (id `agility`): nhanh, lấy chân làm chính, có xoay người
  agility: ['punch', 'kick', 'spinkick', 'sweep', 'tripkick', 'backfist'],
  // Dân chợ búa: dép · tát · xô · húc đầu · chém · vật — đúng chất chợ búa
  street: ['slipper', 'slap', 'shove', 'headbutt', 'chop', 'grapple'],
  // Bắn thuê: báng súng · đấm · chỏ · xô · gối · phản chỏ
  army: ['riflebutt', 'punch', 'elbow', 'shove', 'knee', 'backfist'],
  // Đẻ mướn: dép là vũ khí chính, còn lại là chợ búa
  mother: ['slipper', 'slap', 'headbutt', 'chop', 'shove', 'grapple'],

  // ─────────────────────────────────────────────────────────────── QUÁI
  // Đầu Gấu: đấm móc + húc đầu + vật (đông, thô)
  thug: ['punch', 'hook', 'headbutt', 'grapple', 'shove'],
  // Quái hệ Thân Pháp: đá liên tục, tốc độ
  runner: ['kick', 'punch', 'tripkick', 'sweep'],
  // Đầu Gấu Trùm: chưởng + chém, nặng tay
  brawler: ['palm', 'hook', 'chop', 'shove'],
  // Xạ Thủ: báng súng khi bị áp sát
  gunner: ['riflebutt', 'punch', 'shove'],
  // Mụ Buôn: tát + vụt dép (vũ khí của mụ)
  spawner: ['slap', 'slipper', 'chop'],
  // Đệ tử Mẹ: trẻ con — đấm, đá, tát, húc đầu
  brat: ['punch', 'kick', 'slap', 'headbutt'],
  // Đệ tử tinh nhuệ: nhanh và có đòn xoay
  eliteBrat: ['punch', 'kick', 'spinkick', 'backfist', 'knee'],
  // Trùm màn: bài dài nhất, đủ tay/chân/đầu/vật
  stageBoss: ['palm', 'hook', 'kick', 'headbutt', 'grapple', 'shove'],
};

export interface MoveDef {
  id: string;
  name: string;
  /** tay/ chân/ đầu nào ra đòn */
  limb: 'armR' | 'armL' | 'legR' | 'legL' | 'both' | 'head';
  kind: 'punch' | 'kick' | 'grapple' | 'push' | 'head' | 'weapon';
  /** một câu để đọc code là biết thế này khác thế kia ở đâu (không dùng khi chạy) */
  note?: string;
}

/**
 * Toàn bộ thế võ. `id` ở đây chính là khoá tra bảng dáng trong `art/pose.ts`
 * (xem `hasMeleePose`) — thêm thế mới thì PHẢI thêm case bên đó, nếu không nó
 * sẽ rơi vào dáng `punch` và mọi thế mới lại giống nhau.
 */
export const MOVES: Record<string, MoveDef> = {
  // ───────────────────────────────────────────────────────────────── TAY
  punch: { id: 'punch', name: 'Đấm Thẳng', limb: 'armR', kind: 'punch', note: 'tay phải duỗi thẳng, người xoay tới, hông đẩy' },
  hook: { id: 'hook', name: 'Móc Câu', limb: 'armR', kind: 'punch', note: 'tay phải quét ngang từ ngoài vào, cả người xoay' },
  uppercut: { id: 'uppercut', name: 'Móc Ngược', limb: 'armR', kind: 'punch', note: 'khuỵu người rồi móc từ dưới vọt lên, bật cao' },
  backfist: { id: 'backfist', name: 'Phản Chỏ', limb: 'armR', kind: 'punch', note: 'tay bắt chéo ngực rồi vung ngược mu bàn tay ra' },
  chop: { id: 'chop', name: 'Chém Cạnh Tay', limb: 'armL', kind: 'punch', note: 'tay trái giơ cao chém chéo xuống, người sụp theo' },
  slap: { id: 'slap', name: 'Tát', limb: 'armL', kind: 'punch', note: 'tay trái vả ngang hết tầm, đầu hất theo' },
  slipper: { id: 'slipper', name: 'Vụt Dép', limb: 'armR', kind: 'weapon', note: 'nhón người vung dép qua đầu rồi vụt sụp xuống' },
  elbow: { id: 'elbow', name: 'Chỏ', limb: 'armR', kind: 'grapple', note: 'gập tay, xoay người thúc chỏ tới, bước chân vào' },

  // ────────────────────────────────────────────────────────── HAI TAY
  palm: { id: 'palm', name: 'Chưởng', limb: 'both', kind: 'push', note: 'chưởng phải tới cao, chưởng trái thấp, người lao tới' },
  shove: { id: 'shove', name: 'Xô', limb: 'both', kind: 'push', note: 'hai tay xô tới ngang bụng, hông đẩy theo' },
  grapple: { id: 'grapple', name: 'Vật', limb: 'both', kind: 'grapple', note: 'chụp thấp rồi quăng qua vai, người ưỡn ngả sau' },
  riflebutt: { id: 'riflebutt', name: 'Đập Báng Súng', limb: 'both', kind: 'weapon', note: 'hai tay nâng báng súng qua đầu rồi đập sụp xuống' },

  // ───────────────────────────────────────────────────────────────── CHÂN
  knee: { id: 'knee', name: 'Lên Gối', limb: 'legR', kind: 'kick', note: 'gối phải thúc lên, hai tay kéo mục tiêu xuống' },
  kick: { id: 'kick', name: 'Đá Ngang', limb: 'legR', kind: 'kick', note: 'chân phải tống ngang, người ngả ra sau giữ thăng bằng' },
  spinkick: { id: 'spinkick', name: 'Đá Xoay', limb: 'legR', kind: 'kick', note: 'xoay cả người rồi duỗi chân quét vòng' },
  tripkick: { id: 'tripkick', name: 'Móc Chân', limb: 'legL', kind: 'kick', note: 'chân trái móc ngang tầm thấp, người né ra sau' },
  sweep: { id: 'sweep', name: 'Quét Cước', limb: 'legR', kind: 'kick', note: 'ngồi sụp hẳn xuống rồi quét chân vòng dưới đất' },

  // ───────────────────────────────────────────────────────────────── ĐẦU
  headbutt: { id: 'headbutt', name: 'Húc Đầu', limb: 'head', kind: 'head', note: 'ngửa ra sau rồi chúi húc đỉnh đầu tới, hông đẩy' },
};

export function comboFor(kind: string): string[] {
  return MELEE_COMBOS[kind] ?? ['punch'];
}

export function meleeMoveFor(kind: string, index: number): string {
  const combo = comboFor(kind);
  return combo[((index % combo.length) + combo.length) % combo.length];
}

export function moveName(id: string): string {
  return MOVES[id]?.name ?? id;
}
